<div class="min-h-screen bg-slate-50">
  <div class="mx-auto max-w-[1600px] space-y-2 px-2 py-2 lg:px-0">
    <!-- Title row -->
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <h1 class="text-[28px] font-bold leading-tight tracking-tight text-slate-900">
          {{ generatedProperty() ? 'Edit CI' : 'Create New CI' }}
        </h1>
        <p class="mt-1 text-sm text-slate-500">
          Enter and validate the information required to generate Circled Intelligence
          for this property.
        </p>
      </div>

      <div class="flex shrink-0 items-center gap-3">
        @if (!generatedProperty()) {
        <button
          type="button"
          [disabled]="savingDraft() || generating()"
          (click)="saveDraft()"
          class="h-10 rounded-lg border border-slate-200 bg-white px-5 text-sm font-medium
                 text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none
                 focus-visible:ring-2 focus-visible:ring-indigo-500
                 focus-visible:ring-offset-2 disabled:opacity-50"
        >
          {{ savingDraft() ? 'Saving…' : 'Save Draft' }}
        </button>
        }

        <button
          type="button"
          (click)="cancel()"
          class="h-10 rounded-lg border border-slate-200 bg-white px-5 text-sm font-medium
                 text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none
                 focus-visible:ring-2 focus-visible:ring-indigo-500
                 focus-visible:ring-offset-2"
        >
          Cancel
        </button>
      </div>
    </div>

    @if (loadError()) {
      <ui-note-bar tone="warn">{{ loadError() }}</ui-note-bar>
    }

    <!-- Property type -->
    <ci-property-type
      [selected]="category()"
      (selectedChange)="onCategoryChange($event)"
    />

    <!-- Step pills -->
    <ci-step-pills
      [steps]="steps()"
      [current]="currentStepId()"
      [completed]="completedSteps()"
      [furthestIndex]="furthestIndex()"
      [unlocked]="resumedDraft()"
      (stepSelect)="goToStep($event)"
    />

    <!-- Step card -->
    <ui-card [heading]="cardHeading()" [description]="currentStep().description">
      @switch (currentStepId()) {
        @case ('property') {
          <ci-step-property
            [form]="form.controls.property"
            [category]="category()"
            [landRateControl]="form.controls.landValue.controls.landRatePsf"
            [builtUpAreaControl]="form.controls.buildingValue.controls.builtUpArea"
          />
        }
        @case ('transactions') {
          <ci-step-transactions
            [form]="form.controls.benchmarking"
            [basis]="derivedBasis()"
            [manualRateControl]="form.controls.benchmark.controls.baseBenchmarkPsf"
            [microMarket]="form.controls.property.controls.microMarket.value"
            (addSimilar)="addSimilarProperty()"
            (removeSimilar)="removeSimilarProperty($event)"
          />
        }
        @case ('uds-rental') {
          <ci-step-uds-rental
            [form]="form.controls.udsRental"
            [result]="result()"
          />
        }
        @case ('land-value') {
          <ci-step-land-value
            [form]="form.controls.landValue"
            [category]="category()"
            [plotArea]="form.controls.property.controls.size.value"
            [askingPrice]="form.controls.property.controls.quotedPrice.value"
          />
        }
        @case ('building-value') {
          <ci-step-building-value
            [form]="form.controls.buildingValue"
            [result]="freshResult()"
            [calculating]="calculating()"
            [calculationError]="calculationError()"
          />
        }
        @case ('benchmark') {
          <ci-step-benchmark
            [form]="form.controls.benchmark"
            [basis]="derivedBasis()"
            [questions]="questions()"
            [category]="category()"
            [result]="result()"
            [calculating]="calculating()"
            (overrideToggled)="onOverrideToggled($event)"
          />
        }
        @case ('adjustments') {
          <ci-step-land-adjustments
            [form]="form.controls.landValue"
            [category]="category()"
            [result]="freshResult()"
            [calculating]="calculating()"
            [calculationError]="calculationError()"
          />
        }
        @case ('review') {
          <ci-step-review
            [value]="formValue()"
            [result]="result()"
            [calculating]="calculating()"
            [calculationError]="calculationError()"
            [issues]="calculationIssues()"
            [category]="category()"
          />
        }
      }
    </ui-card>

    @if (error()) {
      <ui-note-bar tone="danger">{{ error() }}</ui-note-bar>
    }

    <!-- Footer actions -->
    <div class="flex flex-wrap items-center justify-between gap-4 pt-2">
      <button
        type="button"
        [disabled]="isFirstStep()"
        (click)="back()"
        class="h-11 rounded-lg border border-slate-200 bg-white px-6 text-sm font-medium
               text-slate-600 transition-colors hover:bg-slate-50 focus:outline-none
               focus-visible:ring-2 focus-visible:ring-indigo-500
               focus-visible:ring-offset-2 disabled:cursor-not-allowed
               disabled:border-transparent disabled:bg-transparent disabled:text-slate-400"
      >
        Back
      </button>

      <div class="flex items-center gap-4">
        @if (!currentStepValid() && !isLastStep()) {
          <span class="hidden text-xs text-slate-500 sm:inline">{{ resumedDraft() ? 'Draft mode: you can continue and complete these fields later.' : blockedMessage() }}</span>
        }

        @if (isLastStep()) {
          <button
            type="button"
            [disabled]="busy() || !canGenerate()"
            (click)="generate()"
            class="inline-flex h-11 items-center gap-2 rounded-lg bg-indigo-600 px-7
                   text-sm font-semibold text-white transition-colors hover:bg-indigo-700
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500
                   focus-visible:ring-offset-2 disabled:cursor-not-allowed
                   disabled:opacity-50"
          >
            @if (busy()) {
              <svg class="size-4 animate-spin" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.6" opacity="0.3" />
                <path d="M14.25 8A6.25 6.25 0 0 0 8 1.75" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" />
              </svg>
              {{ generating() ? 'Generating…' : 'Calculating…' }}
            } @else {
              Generate CI
            }
          </button>
        } @else {
          <button
            type="button"
            [disabled]="!currentStepValid() && !resumedDraft()"
            (click)="next()"
            class="inline-flex h-11 items-center gap-2 rounded-lg bg-indigo-600 px-7
                   text-sm font-semibold text-white transition-colors hover:bg-indigo-700
                   focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500
                   focus-visible:ring-offset-2 disabled:cursor-not-allowed
                   disabled:opacity-50"
          >
            Continue
            <svg viewBox="0 0 16 16" class="size-4" fill="none" aria-hidden="true">
              <path d="m6 4 4 4-4 4" stroke="currentColor" stroke-width="1.6"
                    stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        }
      </div>
    </div>

    @if (pendingCategory(); as nextCategory) {
      <div class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4" role="dialog" aria-modal="true" aria-labelledby="category-switch-title">
        <div class="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
          <h2 id="category-switch-title" class="text-lg font-bold text-slate-900">Save current CI details?</h2>
          <p class="mt-2 text-sm leading-relaxed text-slate-600">
            {{ generatedProperty() ? 'This CI is already generated. Keep editing, or discard unsaved changes before switching property type.' : 'Save the current CI as a draft, or discard unsaved changes before switching property type.' }}
          </p>
          <div class="mt-6 flex flex-wrap justify-end gap-3">
            <button type="button" (click)="keepCurrentCategory()" class="h-10 rounded-lg border border-slate-200 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50">Keep editing</button>
            <button type="button" (click)="discardAndSwitchCategory()" [disabled]="busy()" class="h-10 rounded-lg border border-red-200 bg-white px-4 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50">Discard &amp; switch</button>
            @if (!generatedProperty()) {
            <button type="button" (click)="saveAndSwitchCategory()" [disabled]="savingDraft() || generating()" class="h-10 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">{{ savingDraft() ? 'Saving…' : 'Save draft & switch' }}</button>
            }
          </div>
        </div>
      </div>
    }
  </div>
</div>


@if (showLeaveDialog()) {
  <div class="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/40 p-4"
    role="dialog" aria-modal="true" aria-labelledby="ci-leave-title"
    cdkTrapFocus [cdkTrapFocusAutoCapture]="true" (keydown.escape)="keepEditing()">
    <div class="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
      <h2 id="ci-leave-title" class="text-lg font-bold text-slate-900">Save your CI before leaving?</h2>
      <p class="mt-2 text-sm text-slate-600">You have unsaved CI details. Save a draft to continue later, discard your changes, or keep editing.</p>
      @if (!generatedProperty()) {
        <p class="mt-2 text-xs text-slate-500">Drafts are saved to CI and can be resumed from the CI list. You will stay on this page if saving fails.</p>
      } @else {
        <p class="mt-2 text-xs text-slate-500">This property already has a generated CI and cannot be saved as a draft.</p>
      }
      @if (error()) {
        <p role="alert" class="mt-3 text-sm text-red-600">{{ error() }}</p>
      }
      <div class="mt-6 flex flex-wrap justify-end gap-3">
        <button type="button" cdkFocusInitial (click)="keepEditing()" [disabled]="savingDraft() || generating()"
          class="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 disabled:opacity-50">Keep editing</button>
        <button type="button" (click)="discardAndLeave()" [disabled]="savingDraft() || generating()"
          class="rounded-lg border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 disabled:opacity-50">Discard &amp; leave</button>
        @if (!generatedProperty()) {
          <button type="button" (click)="saveAndLeave()" [disabled]="savingDraft() || generating()"
            class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{{ savingDraft() ? 'Saving…' : 'Save draft & leave' }}</button>
        }
      </div>
    </div>
  </div>
}
